import React from "react";
import { AlertCircle, AlertTriangle, CheckCircle2, Info, Inbox } from "lucide-react";
import "./Feedback.scss";

/** A spinning ring in the accent colour. */
export function Spinner({ size = "md", label, className }: { size?: "sm" | "md" | "lg"; label?: string; className?: string }) {
  return (
    <span className={["ui-spinner", `ui-spinner--${size}`, className].filter(Boolean).join(" ")} role="status" aria-label={label ?? "Loading"}>
      <span className="ui-spinner__ring" aria-hidden="true" />
    </span>
  );
}

/** A centred spinner with an optional line, for a page or panel that is loading. */
export function Loader({ label, minHeight = 240, className }: { label?: React.ReactNode; minHeight?: number; className?: string }) {
  return (
    <div className={["ui-loader", className].filter(Boolean).join(" ")} style={{ minHeight }}>
      <Spinner size="lg" label={typeof label === "string" ? label : undefined} />
      {label ? <p className="ui-loader__label">{label}</p> : null}
    </div>
  );
}

/** A grey shimmering block standing in for content. */
export function Skeleton({ width = "100%", height = 14, radius, className }: { width?: number | string; height?: number | string; radius?: number; className?: string }) {
  return <span className={["ui-skeleton", className].filter(Boolean).join(" ")} style={{ width, height, borderRadius: radius }} aria-hidden="true" />;
}

/** Skeleton rows for a list or table that is loading. */
export function SkeletonRows({ rows = 5, columns = 4 }: { rows?: number; columns?: number }) {
  return (
    <div className="ui-skeleton-rows" role="status" aria-label="Loading">
      {Array.from({ length: rows }).map((_, r) => (
        <div key={r} className="ui-skeleton-rows__row" style={{ gridTemplateColumns: `repeat(${columns}, minmax(0, 1fr))` }}>
          {Array.from({ length: columns }).map((__, c) => (
            <Skeleton key={c} width={c === 0 ? "80%" : "60%"} />
          ))}
        </div>
      ))}
    </div>
  );
}

export type EmptyStateProps = {
  icon?: React.ReactNode;
  title: React.ReactNode;
  description?: React.ReactNode;
  /** A button or two */
  action?: React.ReactNode;
  /** compact for inside cards and tables */
  compact?: boolean;
  className?: string;
};

/** Nothing to show yet: an icon, a line, and the action that fills it. */
export function EmptyState({ icon, title, description, action, compact = false, className }: EmptyStateProps) {
  return (
    <div className={["ui-empty", compact ? "ui-empty--compact" : "", className].filter(Boolean).join(" ")}>
      <span className="ui-empty__icon" aria-hidden="true">
        {icon ?? <Inbox />}
      </span>
      <p className="ui-empty__title">{title}</p>
      {description ? <p className="ui-empty__description">{description}</p> : null}
      {action ? <div className="ui-empty__action">{action}</div> : null}
    </div>
  );
}

const ALERT_ICONS = { info: Info, ok: CheckCircle2, warn: AlertTriangle, danger: AlertCircle };

/** An inline notice in a page, a form or a dialog. */
export function Alert({
  tone = "info",
  title,
  children,
  action,
  className,
}: {
  tone?: "info" | "ok" | "warn" | "danger";
  title?: React.ReactNode;
  children?: React.ReactNode;
  action?: React.ReactNode;
  className?: string;
}) {
  const Icon = ALERT_ICONS[tone];
  return (
    <div className={["ui-alert", `ui-alert--${tone}`, className].filter(Boolean).join(" ")} role={tone === "danger" ? "alert" : "status"}>
      <Icon className="ui-alert__icon" size={18} aria-hidden="true" />
      <div className="ui-alert__text">
        {title ? <p className="ui-alert__title">{title}</p> : null}
        {children ? <div className="ui-alert__body">{children}</div> : null}
      </div>
      {action ? <div className="ui-alert__action">{action}</div> : null}
    </div>
  );
}
